import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "../components/AdminLayout";
import { ApiError } from "@/lib/api";
import { uploadImage } from "@/lib/files";
import {
  listAllEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  formatEventDate,
  type EventItem,
  type WriteEventInput,
} from "@/lib/events";

type FormState = {
  title: string;
  shortDescription: string;
  description: string;
  imageUrl: string;
  startDate: string;
  endDate: string;
  startTime: string;
  location: string;
  city: string;
  externalUrl: string;
  linkedTourSlug: string;
  isFeatured: boolean;
  isPublished: boolean;
  displayOrder: string;
};

const emptyForm: FormState = {
  title: "", shortDescription: "", description: "", imageUrl: "",
  startDate: "", endDate: "", startTime: "", location: "", city: "",
  externalUrl: "", linkedTourSlug: "", isFeatured: false, isPublished: true, displayOrder: "0",
};

function toForm(e: EventItem): FormState {
  return {
    title: e.title,
    shortDescription: e.shortDescription ?? "",
    description: e.description ?? "",
    imageUrl: e.imageUrl ?? "",
    startDate: e.startDate,
    endDate: e.endDate ?? "",
    startTime: e.startTime ? e.startTime.slice(0, 5) : "",
    location: e.location ?? "",
    city: e.city ?? "",
    externalUrl: e.externalUrl ?? "",
    linkedTourSlug: e.linkedTourSlug ?? "",
    isFeatured: e.isFeatured,
    isPublished: e.isPublished,
    displayOrder: String(e.displayOrder),
  };
}

function toWrite(f: FormState): WriteEventInput | { error: string } {
  if (!f.title.trim()) return { error: "events.errorTitleRequired" };
  if (!f.startDate) return { error: "events.errorStartRequired" };
  return {
    title: f.title.trim(),
    shortDescription: f.shortDescription.trim() || null,
    description: f.description.trim() || null,
    imageUrl: f.imageUrl.trim() || null,
    startDate: f.startDate,
    endDate: f.endDate || null,
    startTime: f.startTime ? `${f.startTime}:00` : null,
    location: f.location.trim() || null,
    city: f.city.trim() || null,
    externalUrl: f.externalUrl.trim() || null,
    linkedTourSlug: f.linkedTourSlug.trim() || null,
    isFeatured: f.isFeatured,
    isPublished: f.isPublished,
    displayOrder: parseInt(f.displayOrder, 10) || 0,
  };
}

const input = "w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-ocean";
const label = "block text-xs font-medium text-gray-500 mb-1";

export default function AdminEventosPage() {
  const { t } = useTranslation("events");
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openForm, setOpenForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const onPickImage = async (file: File) => {
    setUploading(true);
    setUploadError(null);
    try {
      const result = await uploadImage(file);
      setForm((f) => ({ ...f, imageUrl: result.url }));
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : t("events.uploadError"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try { setEvents(await listAllEvents()); }
    catch (err) { setError(err instanceof ApiError ? err.message : t("events.loadError")); setEvents([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const sorted = useMemo(() => [...events].sort((a, b) => b.startDate.localeCompare(a.startDate)), [events]);

  const startCreate = () => { setEditingId(null); setForm({ ...emptyForm }); setFormError(null); setOpenForm(true); };
  const startEdit = (e: EventItem) => { setEditingId(e.id); setForm(toForm(e)); setFormError(null); setOpenForm(true); };
  const closeForm = () => { setOpenForm(false); setEditingId(null); setForm(emptyForm); setFormError(null); };

  const handleSave = async () => {
    setFormError(null);
    const payload = toWrite(form);
    if ("error" in payload) { setFormError(t(payload.error)); return; }
    setSaving(true);
    try {
      if (editingId) await updateEvent(editingId, payload);
      else await createEvent(payload);
      await refresh();
      closeForm();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : t("events.errorSave"));
    } finally { setSaving(false); }
  };

  const handleDelete = async (e: EventItem) => {
    if (!window.confirm(t("events.confirmDelete", { title: e.title }))) return;
    try { await deleteEvent(e.id); await refresh(); } catch { /* keep view */ }
  };

  return (
    <AdminLayout title={t("events.adminTitle")}>
      <div className="flex items-center justify-end mb-4">
        <button onClick={startCreate} className="inline-flex items-center gap-2 bg-ocean text-white text-sm font-medium px-4 py-2 rounded-full hover:bg-ocean/90">
          <i className="ri-add-line" /> {t("events.newEvent")}
        </button>
      </div>

      {error && <div className="bg-coral/10 border border-coral/30 text-coral text-sm px-4 py-3 rounded-xl mb-4">{error}</div>}

      {loading ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 animate-pulse h-40" />
      ) : sorted.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <p className="text-sm text-gray-500">{t("events.adminEmpty")}</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase">
              <tr>
                <th className="text-left px-5 py-3">{t("events.colEvent")}</th>
                <th className="text-left px-5 py-3">{t("events.colDate")}</th>
                <th className="text-left px-5 py-3">{t("events.colCity")}</th>
                <th className="text-left px-5 py-3">{t("events.colFeatured")}</th>
                <th className="text-left px-5 py-3">{t("events.colStatus")}</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((e) => (
                <tr key={e.id} className="border-t border-gray-100">
                  <td className="px-5 py-3 font-medium text-charcoal">{e.title}</td>
                  <td className="px-5 py-3 text-gray-500 whitespace-nowrap">{formatEventDate(e)}</td>
                  <td className="px-5 py-3 text-gray-500 whitespace-nowrap">{e.city ?? "—"}</td>
                  <td className="px-5 py-3">{e.isFeatured ? <i className="ri-star-fill text-coral" /> : <span className="text-gray-300">—</span>}</td>
                  <td className="px-5 py-3">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${e.isPublished ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                      {e.isPublished ? t("events.statusPublished") : t("events.statusDraft")}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    <button onClick={() => startEdit(e)} className="text-xs font-medium text-ocean hover:underline mr-3">{t("events.edit")}</button>
                    <button onClick={() => handleDelete(e)} className="text-xs font-medium text-coral hover:underline">{t("events.delete")}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openForm && (
        <div className="fixed inset-0 bg-charcoal/50 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-3.5 max-h-full overflow-y-auto">
            <h2 className="text-lg font-bold text-charcoal">{editingId ? t("events.editEvent") : t("events.newEvent")}</h2>

            <div>
              <label className={label}>{t("events.fieldTitle")}</label>
              <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={input} maxLength={200} />
            </div>
            <div>
              <label className={label}>{t("events.fieldShortDescription")}</label>
              <input type="text" value={form.shortDescription} onChange={(e) => setForm({ ...form, shortDescription: e.target.value })} className={input} maxLength={300} />
            </div>
            <div>
              <label className={label}>{t("events.fieldDescription")}</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className={input} />
            </div>
            <div>
              <label className={label}>{t("events.fieldImageUrl")}</label>
              {/* Events are refreshed weekly, so pasting a URL was the slow part.
                  The upload button reuses the same endpoint the tour form already uses.
                  The text field stays for anyone who does have a link. */}
              <div className="flex gap-2">
                <input type="text" value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} placeholder={t("events.placeholderImageUrl")} className={input} />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void onPickImage(f); }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="shrink-0 border border-gray-200 text-charcoal text-sm px-3 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
                >
                  {uploading ? t("events.uploading") : t("events.uploadImage")}
                </button>
              </div>
              {uploadError && <p className="text-xs text-coral mt-1">{uploadError}</p>}
              {form.imageUrl && (
                <img src={form.imageUrl} alt="" className="mt-2 h-24 w-auto rounded-lg border border-gray-100 object-cover" />
              )}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div><label className={label}>{t("events.fieldStart")}</label><input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} className={input} /></div>
              <div><label className={label}>{t("events.fieldEnd")}</label><input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} className={input} /></div>
              <div><label className={label}>{t("events.fieldTime")}</label><input type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} className={input} /></div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>{t("events.fieldLocation")}</label><input type="text" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={input} maxLength={200} /></div>
              <div><label className={label}>{t("events.fieldCity")}</label><input type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={input} maxLength={100} placeholder="La Paz" /></div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>{t("events.fieldExternalUrl")}</label><input type="text" value={form.externalUrl} onChange={(e) => setForm({ ...form, externalUrl: e.target.value })} className={input} placeholder={t("events.placeholderExternalUrl")} /></div>
              <div><label className={label}>{t("events.fieldTourSlug")}</label><input type="text" value={form.linkedTourSlug} onChange={(e) => setForm({ ...form, linkedTourSlug: e.target.value })} className={input} placeholder={t("events.placeholderTourSlug")} /></div>
            </div>

            <div className="grid grid-cols-2 gap-3 items-end">
              <div><label className={label}>{t("events.fieldOrder")}</label><input type="number" value={form.displayOrder} onChange={(e) => setForm({ ...form, displayOrder: e.target.value })} className={input} /></div>
              <div className="flex flex-col gap-2 pb-1">
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.isFeatured} onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })} className="accent-ocean" /><span className="text-sm text-charcoal">{t("events.fieldFeatured")}</span></label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.isPublished} onChange={(e) => setForm({ ...form, isPublished: e.target.checked })} className="accent-ocean" /><span className="text-sm text-charcoal">{t("events.fieldPublished")}</span></label>
              </div>
            </div>

            {formError && <div className="bg-coral/10 border border-coral/30 text-coral text-sm px-3 py-2 rounded-lg">{formError}</div>}

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={closeForm} disabled={saving} className="flex-1 border border-gray-200 text-charcoal text-sm font-medium px-4 py-2.5 rounded-full hover:bg-gray-50 disabled:opacity-50">{t("events.cancel")}</button>
              <button type="button" onClick={handleSave} disabled={saving} className="flex-1 bg-ocean text-white text-sm font-medium px-4 py-2.5 rounded-full hover:bg-ocean/90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                {t("events.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
